import React, { useState, useEffect } from "react";
import dataService from "../services/dataService";
import {
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
} from "react-icons/fa";
import { IoEyeOutline } from "react-icons/io5";
import { useRef } from "react";
import "../css/contentmanagement.css";
import ConfirmModal from "../components/ConfirmModal";
import { toast } from "react-toastify";

const ContentManagement = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [contentPerPage] = useState(7); // Show 7 items per page to match UserManagement
  const [totalContents, setTotalContents] = useState(0);
  const [loadError, setLoadError] = useState(null);
  // Refreshing an already-rendered list, as opposed to the blocking first load
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    type: "Article",
    author: "",
    status: "Draft",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  // Comment moderation inside the preview modal
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  const Api = process.env.REACT_APP_BACKEND_URL;

  // Debounce the search box before querying the API
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // A new search always starts again from the first page
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Keeps the silent refresh interval on the query the user is looking at
  const queryRef = useRef({ page: 1, search: "" });
  queryRef.current = { page: currentPage, search: debouncedSearch };

  const hasLoadedOnceRef = useRef(false);
  const requestIdRef = useRef(0);

  const modalStatesRef = useRef({ showAddForm, showConfirmModal, showPreview });

  useEffect(() => {
    modalStatesRef.current = { showAddForm, showConfirmModal, showPreview };
  }, [showAddForm, showConfirmModal, showPreview]);

  const loadContent = async (isSilent = false) => {
    const token = localStorage.getItem("access_token");

    // Only the very first load may blank the page. Every later query (typing in
    // the search box, changing page) refreshes the table in place, so the
    // control bar is never unmounted and the search box keeps focus.
    if (!isSilent) {
      if (hasLoadedOnceRef.current) setIsRefreshing(true);
      else setLoading(true);
    }

    // Ignore a response that a newer query has already superseded, so slow
    // replies cannot overwrite the results of what is now in the search box.
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;

    // Search and paging run on the server, otherwise the page can only ever see
    // the first response page worth of posts.
    const { page, search } = queryRef.current;
    const baseUrl = Api.endsWith("/") ? Api : `${Api}/`;
    const params = new URLSearchParams({
      skip: String((page - 1) * contentPerPage),
      limit: String(contentPerPage),
    });
    if (search) params.append("search", search);

    try {
      const response = await fetch(`${baseUrl}admin/content?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to load content (${response.status})`);
      const result = await response.json();
      if (!isCurrent()) return;
      const newData = result.data || [];

      // Deep compare to prevent unnecessary re-renders that cause "jerking"
      setContents(prev => {
        if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
        return newData;
      });
      setTotalContents(result.total || 0);
      setLoadError(null);
    } catch (error) {
      console.error("Error loading content:", error);
      if (!isSilent && isCurrent()) {
        setLoadError("Could not load content. Please try again.");
      }
    } finally {
      if (isCurrent()) {
        hasLoadedOnceRef.current = true;
        if (!isSilent) {
          setLoading(false);
          setIsRefreshing(false);
        }
      }
    }
  };

  const loadComments = async (postId) => {
    const token = localStorage.getItem("access_token");
    setCommentsLoading(true);
    setCommentsError(null);
    try {
      const baseUrl = Api.endsWith("/") ? Api : `${Api}/`;
      const response = await fetch(`${baseUrl}admin/posts/${postId}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Failed to load comments (${response.status})`);
      const result = await response.json();
      setComments(result.data || []);
    } catch (error) {
      console.error("Error loading comments:", error);
      setComments([]);
      setCommentsError("Could not load comments.");
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    const token = localStorage.getItem("access_token");
    setDeletingCommentId(commentId);
    try {
      const baseUrl = Api.endsWith("/") ? Api : `${Api}/`;
      const response = await fetch(`${baseUrl}admin/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        toast.error(result.detail || result.message || "Failed to delete comment");
        return;
      }

      toast.success(result.message || "Comment deleted successfully");

      // Deleting a comment also removes its replies, so re-read the thread
      // instead of guessing which rows disappeared.
      const postId = selectedPost && selectedPost.post_id;
      if (postId) await loadComments(postId);

      // Sync the count the backend just recalculated so the modal and the
      // table row agree without a full reload.
      const newCount = result.comments_count;
      if (typeof newCount === "number") {
        setSelectedPost((prev) =>
          prev ? { ...prev, comments_count: newCount } : prev
        );
        setContents((prev) =>
          prev.map((c) => (c.post_id === postId ? { ...c, comments_count: newCount } : c))
        );
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("An error occurred");
    } finally {
      setDeletingCommentId(null);
    }
  };

  useEffect(() => {
    loadContent();

    // Stable interval that doesn't re-create itself
    const interval = setInterval(() => {
      const { showAddForm, showConfirmModal, showPreview } = modalStatesRef.current;
      if (!showAddForm && !showConfirmModal && !showPreview) {
        loadContent(true);
      }
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Api, currentPage, contentPerPage, debouncedSearch]);

  useEffect(() => {
    if (showAddForm || showConfirmModal || showPreview) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAddForm, showConfirmModal, showPreview]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingContent) {
      await dataService.updateContent(editingContent.id, formData);
      setEditingContent(null);
      toast.success("Content updated successfully");
      setTimeout(() => window.location.reload(), 1000);
    } else {
      await dataService.addContent(formData);
      setShowAddForm(false);
      toast.success("Content created successfully");
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  // Handle delete content
  const handleDelete = async (contentId) => {
    try {
      setIsDeleting(true);
      const token = localStorage.getItem("access_token");
      const baseUrl = Api.endsWith("/") ? Api : `${Api}/`;
      const response = await fetch(`${baseUrl}admin/posts/${contentId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success("Content deleted successfully");
        setShowConfirmModal(false);
        setDeleteTarget(null);
        await loadContent(true);
      } else {
        const result = await response.json().catch(() => ({}));
        toast.error(result.detail || "Failed to delete content");
      }
    } catch (error) {
      console.error("Error deleting content:", error);
      toast.error("An error occurred");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderComment = (comment, isReply) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
      <img
        src={
          comment.user_profile_image
            ? `${Api.replace(/\/$/, "")}${comment.user_profile_image}`
            : `https://ui-avatars.com/api/?name=${comment.user_name || "User"}&background=ff2d2d&color=fff`
        }
        alt=""
        style={{
          width: isReply ? "24px" : "28px",
          height: isReply ? "24px" : "28px",
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{ fontWeight: "700", fontSize: isReply ? "0.78rem" : "0.82rem" }}>
            {comment.user_name || "Unknown"}
          </span>
          <span style={{ fontSize: "0.68rem", color: "#999" }}>
            {comment.created_at ? new Date(comment.created_at).toLocaleString() : ""}
          </span>
        </div>
        <p style={{ margin: "4px 0 0", fontSize: isReply ? "0.8rem" : "0.85rem", color: "#333", wordBreak: "break-word" }}>
          {comment.comment_text}
        </p>
      </div>
      <button
        className="action-icon-btn delete-btn"
        onClick={() => handleDeleteComment(comment.comment_id)}
        disabled={deletingCommentId === comment.comment_id}
        title={isReply ? "Delete reply" : "Delete comment"}
        style={{
          background: "transparent",
          border: "none",
          color: "#ff2d2d",
          cursor: deletingCommentId === comment.comment_id ? "not-allowed" : "pointer",
          opacity: deletingCommentId === comment.comment_id ? 0.5 : 1,
          padding: "4px",
          flexShrink: 0,
        }}
      >
        <FaTrash size={12} />
      </button>
    </div>
  );

  const renderReplies = (replies) => {
    if (!replies || replies.length === 0) return null;
    return (
      <div style={{ marginTop: "10px", paddingLeft: "14px", borderLeft: "2px solid #eee", display: "flex", flexDirection: "column", gap: "10px" }}>
        {replies.map((reply) => (
          <div key={reply.comment_id}>
            {renderComment(reply, true)}
            {renderReplies(reply.replies)}
          </div>
        ))}
      </div>
    );
  };

  const closePreview = () => {
    setShowPreview(false);
    setSelectedPost(null);
    setComments([]);
    setCommentsError(null);
  };

  // The API already applied the search and the page window
  const currentItems = contents || [];
  const totalPages = Math.max(1, Math.ceil(totalContents / contentPerPage));

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="theme-loader"></div>
      </div>
    );
  }

  return (
    <div className="content-management-container">
      <div className="control-bar">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search content, users..."
            className="search-input-field"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {/* <div className="action-buttons">
          <button
            className="all-content-btn"
            onClick={() => setShowAddForm(true)}
          >
            Create New Post
          </button>
        </div> */}
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="modal-overlay-content">
          <form className="modal-form-content" onSubmit={handleSubmit}>
            <h2 className="modal-title-content">
              {editingContent ? "Edit Content" : "Create New Content"}
            </h2>
            <div className="form-grid-content">
              <input
                type="text"
                placeholder="Title"
                className="form-input-content"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
              />
              <select
                className="form-input-content"
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
              >
                <option value="Article">Article</option>
                <option value="Video">Video</option>
                <option value="Audio">Audio</option>
              </select>
              <input
                type="text"
                placeholder="Author"
                className="form-input-content"
                value={formData.author}
                onChange={(e) =>
                  setFormData({ ...formData, author: e.target.value })
                }
                required
              />
              <select
                className="form-input-content"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Draft">Draft</option>
                <option value="Published">Published</option>
              </select>
            </div>
            <div className="modal-footer-content">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingContent(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#ff2d2d",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                {editingContent ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table Card */}
      <div className={`table-card${isRefreshing ? " is-refreshing" : ""}`}>
        <div className="table-scroll-wrapper">
          <div className="table-header">
            <span style={{ textAlign: "center" }}>S.No</span>
            <span>User</span>
            <span>Email</span>
            <span>Captions</span>
            <span style={{ textAlign: "center" }}>Views</span>
            <span style={{ textAlign: "center" }}>Likes</span>
            <span style={{ textAlign: "center" }}>Comments</span>
            <span style={{ textAlign: "center" }}>Created At</span>
            <span style={{ textAlign: "center" }}>Action</span>
          </div>

          {loadError ? (
            <div className="empty-state">
              <div className="empty-state-icon">!</div>
              <p>{loadError}</p>
            </div>
          ) : currentItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">&#128196;</div>
              <p>{debouncedSearch ? "No posts match your search" : "No posts yet"}</p>
            </div>
          ) : (
            currentItems.map((content, index) => (
            <div
              key={content.post_id || index}
              className="table-row"
              style={{ alignItems: "center" }}
            >
              <span style={{ textAlign: "center" }}>{(currentPage - 1) * contentPerPage + index + 1}</span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={
                    content.user_profile_image
                      ? `${Api.replace(/\/$/, "")}${content.user_profile_image}`
                      : `https://ui-avatars.com/api/?name=${content.user_name}&background=ff2d2d&color=fff`
                  }
                  alt=""
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontWeight: "600",
                    textTransform: "uppercase",
                    fontSize: "0.8rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {content.user_name}
                </span>
              </div>
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {content.email}
              </span>
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={content.post_caption}
              >
                {content.post_caption || "No caption"}
              </span>
              <span style={{ textAlign: "center", fontWeight: "500" }}>
                {content.views || 0}
              </span>
              <span style={{ textAlign: "center", fontWeight: "500" }}>
                {content.likes || 0}
              </span>
              <span style={{ textAlign: "center", fontWeight: "500" }}>
                {content.comments_count || 0}
              </span>
              <span style={{ textAlign: "center", color: "#666", fontSize: "0.75rem" }}>
                {new Date(content.created_at).toLocaleDateString()}
              </span>
              <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
                <button
                  className="action-icon-btn view-btn"
                  onClick={() => {
                    setSelectedPost(content);
                    setShowPreview(true);
                    setComments([]);
                    loadComments(content.post_id);
                  }}
                  title="View Post"
                >
                  <IoEyeOutline size={18} />
                </button>
                <button
                  className="action-icon-btn delete-btn"
                  onClick={() => {
                    setDeleteTarget(content);
                    setShowConfirmModal(true);
                  }}
                  title="Delete"
                >
                  <FaTrash size={15} />
                </button>
              </div>
            </div>
            ))
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="pagination-container">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: "8px 12px",
            border: "none",
            backgroundColor:
              currentPage === 1 ? "rgba(255,255,255,0.5)" : "white",
            borderRadius: "8px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            color: currentPage === 1 ? "#999" : "#333",
          }}
        >
          <FaChevronLeft />
        </button>
        {Array.from(
          { length: totalPages },
          (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              style={{
                padding: "8px 14px",
                border: "none",
                backgroundColor: currentPage === i + 1 ? "#ff2d2d" : "white",
                color: currentPage === i + 1 ? "white" : "#333",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              {i + 1}
            </button>
          )
        )}
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={
            currentPage === totalPages
          }
          style={{
            padding: "8px 12px",
            border: "none",
            backgroundColor:
              currentPage ===
                totalPages
                ? "rgba(255,255,255,0.5)"
                : "white",
            borderRadius: "8px",
            cursor:
              currentPage ===
                totalPages
                ? "not-allowed"
                : "pointer",
            color:
              currentPage ===
                totalPages
                ? "#999"
                : "#333",
          }}
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Confirm Delete Modal */}
      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() => handleDelete(deleteTarget.id || deleteTarget.post_id)}
          title="Delete Post"
          message={`Are you sure you want to delete this post? This action cannot be undone.`}
          confirmText={isDeleting ? "Deleting..." : "Delete"}
          isDeleting={isDeleting}
        />
      )}

      {/* Post Preview Modal */}
      {showPreview && selectedPost && (
        <div
          className="modal-overlay-content"
          onClick={closePreview}
          style={{ zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
        >
          <div
            className="modal-form-content"
            style={{
              width: "500px",
              maxWidth: "100%",
              padding: 0,
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: "20px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.3)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed at top */}
            <div style={{ padding: "1.2rem 1.5rem", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, backgroundColor: "#fff" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700" }}>Post Preview</h3>
              <button
                onClick={closePreview}
                style={{ background: "transparent", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#888" }}
              >&times;</button>
            </div>

            {/* Scrollable Container */}
            <div style={{ overflowY: "auto", flex: 1, backgroundColor: "#fff" }}>
              {/* Media Content */}
              <div style={{ backgroundColor: "#000", position: "relative", minHeight: "300px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                {selectedPost.attachments && selectedPost.attachments.length > 0 ? (
                  <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {selectedPost.attachments.map((att, idx) => {
                      const isVideo = att.type && (att.type.includes('video') || att.url.toLowerCase().endsWith('.mp4') || att.url.toLowerCase().endsWith('.mov'));
                      return (
                        <div key={idx} style={{ width: "100%", borderBottom: att.url ? "1px solid #333" : "none" }}>
                          {isVideo ? (
                            <video
                              src={`${Api.replace(/\/$/, '')}${att.url}`}
                              controls
                              style={{ width: "100%", maxHeight: "400px", display: "block" }}
                            />
                          ) : att.url ? (
                            <img
                              src={`${Api.replace(/\/$/, '')}${att.url}`}
                              alt={`Attachment ${idx + 1}`}
                              style={{ width: "100%", maxHeight: "400px", objectFit: "contain", display: "block" }}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: "2rem", color: "#666", textAlign: "center" }}>
                    <p>No media content available for this post.</p>
                  </div>
                )}
              </div>

              {/* Post Info */}
              <div style={{ padding: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "1rem" }}>
                  <img
                    src={
                      selectedPost.user_profile_image
                        ? `${Api.replace(/\/$/, '')}${selectedPost.user_profile_image}`
                        : `https://ui-avatars.com/api/?name=${selectedPost.user_name}&background=ff2d2d&color=fff`
                    }
                    alt=""
                    style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
                  />
                  <div>
                    <h4 style={{ margin: 0, fontSize: "0.95rem", fontWeight: "700" }}>{selectedPost.user_name}</h4>
                    <p style={{ margin: 0, fontSize: "0.75rem", color: "#888" }}>{new Date(selectedPost.created_at).toLocaleString()}</p>
                  </div>
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <p style={{ margin: 0, fontSize: "0.9rem", color: "#333", lineHeight: "1.6" }}>
                    {selectedPost.post_caption || <span style={{ color: "#999", fontStyle: "italic" }}>No caption provided</span>}
                  </p>
                </div>

                {/* Stats Grid */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", backgroundColor: "#f8f9fa", padding: "1.2rem", borderRadius: "16px", border: "1px solid #f0f0f0" }}>
                  <div style={{ textAlign: "center" }}>
                    <label style={{ fontSize: "0.65rem", color: "#999", textTransform: "uppercase", fontWeight: "800", letterSpacing: "0.5px" }}>Views</label>
                    <p style={{ margin: "4px 0 0", fontWeight: "750", fontSize: "1.1rem", color: "#1a1a1a" }}>{selectedPost.views || 0}</p>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <label style={{ fontSize: "0.65rem", color: "#999", textTransform: "uppercase", fontWeight: "800", letterSpacing: "0.5px" }}>Likes</label>
                    <p style={{ margin: "4px 0 0", fontWeight: "750", fontSize: "1.1rem", color: "#1a1a1a" }}>{selectedPost.likes || 0}</p>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <label style={{ fontSize: "0.65rem", color: "#999", textTransform: "uppercase", fontWeight: "800", letterSpacing: "0.5px" }}>Comments</label>
                    <p style={{ margin: "4px 0 0", fontWeight: "750", fontSize: "1.1rem", color: "#1a1a1a" }}>{selectedPost.comments_count || 0}</p>
                  </div>
                </div>

                {/* Comment Moderation */}
                <div style={{ marginTop: "1.5rem" }}>
                  <h4 style={{ margin: "0 0 0.8rem", fontSize: "0.95rem", fontWeight: "700", color: "#1a1a1a" }}>
                    Comments
                  </h4>

                  {commentsLoading ? (
                    <p style={{ margin: 0, fontSize: "0.85rem", color: "#888" }}>Loading comments...</p>
                  ) : commentsError ? (
                    <p style={{ margin: 0, fontSize: "0.85rem", color: "#991b1b" }}>{commentsError}</p>
                  ) : comments.length === 0 ? (
                    <p style={{ margin: 0, fontSize: "0.85rem", color: "#999", fontStyle: "italic" }}>
                      No comments on this post.
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {comments.map((comment) => (
                        <div
                          key={comment.comment_id}
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #f0f0f0",
                            borderRadius: "12px",
                            padding: "0.8rem 1rem",
                          }}
                        >
                          {renderComment(comment, false)}

                          {renderReplies(comment.replies)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer - Fixed at bottom */}
            <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid #eee", textAlign: "right", flexShrink: 0, backgroundColor: "#fff" }}>
              <button
                onClick={closePreview}
                style={{
                  padding: "12px 24px",
                  borderRadius: "12px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "none",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseOver={(e) => e.target.style.opacity = "0.9"}
                onMouseOut={(e) => e.target.style.opacity = "1"}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
